<template>
  <div class="Tenants">
    <b-row class="mb-4 align-items-end">
      <b-col class="text-left">
        <h1>Tenants</h1>
      </b-col>

      <b-col cols="2" class="text-right"
        ><b-button
          class="Tenants-new-button"
          data-cy="Tenants-new-button"
          variant="outline-primary"
          :disabled="!canCreateTenants"
          @click="showNewTenantModal"
          ><i class="fas fa-plus"></i>&nbsp;Create a new tenant</b-button
        ></b-col
      >
    </b-row>
    <b-overlay :show="isLoading" spinner-variant="primary" opacity="1">
      <Table
        class="Tenants-list p-0 text-left"
        v-if="canSearchTenants"
        filterable
        :items="tenants"
        :fields="fields"
        :current-page="currentPage"
        :per-page="perPage"
        :total-rows="totalRows"
        @filtered="onFilterChanged"
        @sort-changed="onSortChanged"
        @page-changed="onPageChanged"
        @per-page-changed="onPerPageChanged"
      >
        <template v-slot:cell(actions)="data">
          <div class="text-center">
            <a
              href="#"
              v-if="canDeleteTenants"
              :data-cy="`delete-action-${data.item.name}`"
              @click="deleteTenant(data.item.name, data.item.group)"
            >
              <i class="fas fa-trash" />
            </a>
          </div>
        </template>
        <template #empty>
          <h4 class="text-secondary text-center mt-1">
            There is currently no tenant.
          </h4>
          <p class="text-secondary text-center">
            You can create a new one by hitting the button above.
          </p>
        </template>
      </Table>
      <ListNotAllowed v-if="rights.length && !canSearchTenants" />
    </b-overlay>
    <new-tenant-modal :groups="groups" @form-submitted="createTenant" />
  </div>
</template>

<script>
import NewTenantModal from '../components/NewTenantModal';
import ListNotAllowed from '../components/ListNotAllowed';
import { mapGetters, mapMutations } from 'vuex';
import { isActionAllowed } from '@/helpers/rights.js';

export default {
  name: 'Tenants',
  components: {
    NewTenantModal,
    ListNotAllowed
  },

  computed: {
    ...mapGetters('auth', ['currentUserRights']),
    canSearchTenants() {
      return isActionAllowed(
        this.currentUserRights,
        'document',
        'search',
        'multi-tenancy',
        'tenants'
      );
    },
    canCreateTenants() {
      return (
        isActionAllowed(
          this.currentUserRights,
          'multi-tenancy/tenant',
          'create'
        ) &&
        isActionAllowed(
          this.currentUserRights,
          'multi-tenancy/tenant',
          'listGroups'
        )
      );
    },
    canDeleteTenants() {
      return isActionAllowed(
        this.currentUserRights,
        'multi-tenancy/tenant',
        'delete'
      );
    }
  },

  data() {
    return {
      rights: [],
      isLoading: false,
      tenants: [],
      groups: [],
      filter: null,
      currentPage: 1,
      perPage: 10,
      totalRows: 0,
      sort: null,
      fields: [
        {
          key: 'name',
          sortable: true
        },
        {
          key: 'group',
          sortable: true
        },
        {
          key: 'actions',
          label: ''
        }
      ]
    };
  },

  async mounted() {
    const promises = [];

    if (this.canSearchTenants) {
      promises.push(this.fetchTenants());
    }

    if (this.canCreateTenants) {
      promises.push(this.fetchGroups());
    }

    await Promise.all(promises);
  },

  methods: {
    ...mapMutations('tenants', ['ADD_TENANT', 'REMOVE_TENANT']),
    async fetchTenants() {
      const body = {};

      body.sort = this.sort || { name: { order: 'asc' } };

      if (this.filter) {
        body.query = {
          multi_match: {
            query: this.filter,
            type: 'phrase_prefix',
            fields: ['name.text', 'group.text']
          }
        };
      }

      try {
        this.isLoading = true;

        const searchResult = await this.$kuzzle.document.search(
          'multi-tenancy',
          'tenants',
          body,
          {
            from: (this.currentPage - 1) * this.perPage,
            size: this.perPage
          }
        );

        this.totalRows = searchResult.total;

        this.tenants = searchResult.hits.map(el => {
          return {
            _id: el._id,
            name: el._source.name,
            group: el._source.group
          };
        });
      } catch (error) {
        this.$log.error(error);
        this.showErrorToast(error.message);
      }

      this.isLoading = false;
    },
    async fetchGroups() {
      const request = {
        controller: 'multi-tenancy/tenant',
        action: 'listGroups'
      };

      try {
        const response = await this.$kuzzle.query(request);
        this.groups = response.result.groups;
      } catch (error) {
        this.$log.error(error);
        this.showErrorToast(error.message);
      }
    },
    handleDeleteConfirmation(name, group) {
      const customMessage = this.$createElement('p', [
        'Are you sure you want to delete the ',
        this.$createElement('code', name),
        ' tenant of group ',
        this.$createElement('code', group)
      ]);
      return this.$bvModal.msgBoxConfirm(customMessage);
    },
    async deleteTenant(name, group) {
      try {
        const isDeleteConfirmed = await this.handleDeleteConfirmation(
          name,
          group
        );

        if (!isDeleteConfirmed) {
          return;
        }

        this.isLoading = true;

        const request = {
          controller: 'multi-tenancy/tenant',
          action: 'delete',
          name,
          group
        };

        const response = await this.$kuzzle.query(request);

        this.REMOVE_TENANT(response.result.index);

        await this.fetchTenants();
      } catch (error) {
        this.$log.error(error);
        this.showErrorToast(error.message);
      }

      this.isLoading = false;
    },
    async createTenant(formData) {
      try {
        this.isLoading = true;

        const request = {
          controller: 'multi-tenancy/tenant',
          action: 'create',
          name: formData.name,
          group: formData.group
        };

        const response = await this.$kuzzle.query(request);

        const index = response.result.index;
        this.ADD_TENANT({
          _id: index,
          group: formData.goup,
          name: formData.name,
          index
        });

        await this.fetchTenants();
      } catch (error) {
        this.$log.error(error);
        this.showErrorToast(error.message);
      }

      this.isLoading = false;
    },
    async onFilterChanged(value) {
      this.filter = value;
      await this.fetchTenants();
    },
    async onSortChanged(value) {
      this.sort = {
        [value.sortBy]: { order: value.sortDesc ? 'desc' : 'asc' }
      };
      await this.fetchTenants();
    },
    async onPageChanged(value) {
      this.currentPage = value;
      await this.fetchTenants();
    },
    async onPerPageChanged(value) {
      this.perPage = value;
      await this.fetchTenants();
    },
    showErrorToast(message) {
      this.$bvToast.toast(message, {
        title: 'Sorry, an error has occurred.',
        variant: 'danger',
        toaster: 'b-toaster-bottom-right',
        appendToast: true,
        dismissible: true,
        noAutoHide: true
      });
    },
    showNewTenantModal() {
      this.$bvModal.show('new-tenant-modal');
    }
  }
};
</script>
